import { cloneDeep, fromPairs, isUndefined } from "lodash-es";
import React, { useMemo, useState } from "react";
import { Field, Form } from "react-final-form";
import { Alert, Button, Card, CardBody, CardHeader, CardTitle, Col, Collapse, FormText, Row } from "reactstrap";
import { DetailedErrorAlert } from "../../../components/DetailedErrorAlert";
import { BlankableField } from "../../../components/inputs/final_form/BlankableField";
import { SelectInput } from "../../../components/inputs/final_form/SelectInput";
import { Switch } from "../../../components/inputs/final_form/Switch";
import { TextInput } from "../../../components/inputs/final_form/TextInput";
import { LoadingButton } from "../../../components/LoadingButton";
import { adminApi as api } from "../../../util/api";
import { GLOBAL_ROLES } from "../../../util/data/global_roles";
import { formatDateTime, objectToOptions } from "../../../util/formatters";
import { camelizeKeys, snakifyKeys } from "../../../util/helpers";
import { composeValidators, numericInRange, required } from "../../../util/validators";
import { AdminOptionableInput } from "../../components/inputs/AdministrateSelectInput.js";
import { usePermissionCheck, useUpdatePermission } from "../../utils";
import { Fieldset } from "../../../components/inputs/final_form/Fieldset.js";
import { FormRow } from "../../../components/forms/FormRow.js";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { OnChange } from "react-final-form-listeners";
import { DonutChart } from "../../../components/DonutChart.js";
import { Toolbar } from "../../../components/Toolbar.js";

const globalRoleOptions = objectToOptions(GLOBAL_ROLES);

const payorPathOptions = window.configuredPayorPaths.map((el) => ({
  label: el.path,
  value: el.id,
}));

const CasePercentageDonutChart = ({ fraction, target, total, className }) => {
  const noData = fraction == null;
  const onshoreAmount = noData ? 0.33 : fraction;
  const offshoreAmount = noData ? 0.67 : 1 - onshoreAmount;

  const data = [
    { x: "Onshore", y: onshoreAmount, total },
    { x: "Offshore", y: offshoreAmount, total },
  ];

  const legend = data.map((d) => {
    const value = noData ? "N/A%" : `${(d.y * 100).toFixed(0)}%`;
    return { name: `${value} ${Math.floor(d.y * target)} / ${Math.floor(d.total)} ${d.x}` };
  });

  const color = noData ? "secondary" : "primary";

  return (
    <div className={className}>
      <DonutChart color={color} data={data} legend={legend} />
    </div>
  );
};

const labToOption = (lab) => ({ value: lab.id, label: lab.dashboard_display_name });

// This function essentially manually filters and updates
// the resulting json object into something consumable
// serverside. We grab the values we are sending to the api. We filter out the trainedLabTraining objects
// to be just the labs with ids from the trainedLabIds(react field)
// Then we check the activatedLabs, if there are any, we set the activated to true on the trainedLabTraining objects
const transformValues = (values) => {
  const newValues = cloneDeep(values);
  newValues.trainedLabTrainings = newValues.trainedLabTrainings.filter((lab) => newValues.trainedLabIds.includes(lab.lab_id));

  newValues.trainedLabTrainings.map((trainingObject) => {
    handleActivation(trainingObject, newValues.activatedLabIds);
  });

  newValues.trainedLabTrainingsAttributes = newValues.trainedLabTrainings;
  delete newValues.activatedLabIds;

  // Hard set sales lead to false if we are not a sales manager
  if (newValues.globalRole !== "sales_manager" && newValues.salesLead) {
    newValues.salesLead = false;
  }

  return newValues;
};

const handleActivation = (trainingObject, activatedLabIds) => {
  activatedLabIds.includes(trainingObject.lab_id) ? (trainingObject.activated = true) : (trainingObject.activated = false);
};

export const UsersGeneralTab = ({ account, accountId, refresh }) => {
  const [canUpdate] = useUpdatePermission("Account", accountId);
  const [canUpdateLabs] = usePermissionCheck("update_trained_activated_labs", "Account", accountId);
  const [canUpdateUserNotes] = usePermissionCheck("update_user_notes", "Account", accountId);

  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [trainingLabOptions, setTrainingLabOptions] = useState(null);

  const [showAutoAssignmentDetails, setShowAutoAssignmentDetails] = useState(false);

  const toggleAutoAssignmentDetails = () => {
    setShowAutoAssignmentDetails(!showAutoAssignmentDetails);
  };

  const handleUpdate = async (values) => {
    setError(null);
    setMessage(null);

    try {
      await api.put(`/admin/accounts/${accountId}/update_with_user`, { account: snakifyKeys(transformValues(values)) });

      setMessage(`Updated user account.`);
      refresh();
    } catch (err) {
      setError(err);
    }
  };

  const initialValues = useMemo(() => camelizeKeys(account), [account]);

  // Grabbing the initial trained Lab options
  const initialTrainedLabOptions = useMemo(() => {
    return account.trained_labs?.map(labToOption) ?? [];
  }, [account]);

  const readOnly = !canUpdate;
  const readOnlyLabs = !canUpdateLabs;
  const readOnlyUserNotes = !canUpdateUserNotes;

  const handleAutoAssignValues = (name, form, value) => {
    if (name === "newCasesPercentage") {
      const amount = 100 - value;

      form.change("dueCasesPercentage", amount);
    }

    if (name === "dueCasesPercentage") {
      const amount = 100 - value;

      form.change("newCasesPercentage", amount);
    }

    form.change(name, value);
  };

  const handleOffshoreChange = (form) => {
    form.change("onshoreCasePercentage", 0);
  };

  return (
    <>
      <DetailedErrorAlert error={error} />
      {message && <Alert color="info">{message}</Alert>}

      <Form onSubmit={handleUpdate} initialValues={initialValues}>
        {({ form, handleSubmit, values, submitting, pristine }) => {
          // NOTE: These happen on every render, we can probably optimize with useMemo, but #hotfix
          const showInternalInputs = Boolean(values.globalRole);
          const activatedLabOptions = trainingLabOptions?.filter((lab) => values.trainedLabIds.includes(lab.value));

          const currentNewCaseFraction = values.newCasesPercentage / 100;

          const currentNewCaseAmount = Math.floor(values.targetCaseAssignmentCount * currentNewCaseFraction);
          const currentDueCaseAmount = values.targetCaseAssignmentCount - currentNewCaseAmount;

          const currentNewCaseOnshoreFraction = parseFloat(values.onshoreCasePercentage / 100).toPrecision(2);
          const currentDueCaseOnshoreFraction = currentNewCaseOnshoreFraction;

          return (
            <>
              <Field component={TextInput} required validate={required} label="ID" name="id" readOnly />
              <Field component={TextInput} label="Email/Username" name="email" readOnly />
              <Field component={TextInput} required validate={required} label="First Name" name="firstName" readOnly={readOnly} />
              <Field component={TextInput} required validate={required} label="Last Name" name="lastName" readOnly={readOnly} />
              <BlankableField
                component={SelectInput}
                options={globalRoleOptions}
                includeBlank={true}
                label="Global Role"
                name="globalRole"
                readOnly={readOnly}
              />

              {values.globalRole === "sales_manager" && (
                <Field type="checkbox" component={Switch} label={"Sales Lead"} name="salesLead" readOnly={readOnly} />
              )}

              <Field type="checkbox" component={Switch} label="Quarantine" name="quarantine" id="quarantine" readOnly={readOnly} />
              <Field type="checkbox" component={Switch} label="Active" name="active" id="active" readOnly={readOnly} />

              <Field type="checkbox" component={Switch} label="Offshore" name="offshore" id="offshore" readOnly={readOnly} />
              <OnChange name="offshore">
                {(value, previous) => {
                  if (!value) return;
                  handleOffshoreChange(form);
                }}
              </OnChange>

              <Field type="checkbox" component={Switch} label="Is Robot" name="isRobot" id="isRobot" readOnly={readOnly} />

              <Field type="checkbox" component={Switch} label={"Show on Assign/Reassign Queue"} name="showInQueue" readOnly={readOnly} />

              {showInternalInputs && (
                <>
                  <Field
                    type="checkbox"
                    component={Switch}
                    label={"Process New Cases first in My Cases"}
                    name="processNewCasesFirst"
                    readOnly={readOnly}
                  />

                  <Field
                    component={SelectInput}
                    label="Payor Path Defaults"
                    name="payorPathIds"
                    options={payorPathOptions}
                    isMulti
                    readOnly={readOnlyLabs}
                  />

                  <Fieldset legend="Lab Configuration">
                    <Field
                      component={AdminOptionableInput}
                      resource="labs"
                      onOptionsChange={setTrainingLabOptions}
                      per={100}
                      kept={true}
                      label="Trained Labs"
                      name="trainedLabIds"
                      initialOptions={initialTrainedLabOptions}
                      isMulti
                      readOnly={readOnlyLabs}
                      scope="default"
                    />
                    <Field
                      component={SelectInput}
                      label="Activated Labs"
                      name="activatedLabIds"
                      options={activatedLabOptions}
                      isMulti
                      readOnly={readOnlyLabs}
                    />
                  </Fieldset>

                  <Fieldset legend="Case Auto Assignment Configuration">
                    <Row>
                      <Col>
                        <BlankableField
                          component={TextInput}
                          type="number"
                          min={1}
                          max={120}
                          step={1}
                          required
                          validate={composeValidators(numericInRange(1, 120))}
                          label="Target Number of Assigned Cases"
                          name="targetCaseAssignmentCount"
                          readOnly={readOnly}
                        >
                          <FormText>Total amount of cases assigned.</FormText>
                        </BlankableField>
                      </Col>

                      <Col>
                        <Field
                          component={TextInput}
                          type="number"
                          label="% of Onshore cases"
                          name="onshoreCasePercentage"
                          readOnly={readOnly}
                          disabled={readOnly || values.offshore}
                          min={0}
                          max={100}
                          step={1}
                          validate={composeValidators(numericInRange(0, 100))}
                        >
                          <FormText>
                            {values.offshore ? (
                              <>Field disabled for offshore accounts.</>
                            ) : (
                              <>Percentage of onshore cases on assignment, the rest will be offshore if they're available.</>
                            )}
                          </FormText>
                        </Field>
                      </Col>

                      <Col>
                        <Field
                          component={TextInput}
                          type="number"
                          label="% New Auto Assigned"
                          name="newCasesPercentage"
                          readOnly={readOnly}
                          disabled={readOnly}
                          min={0}
                          max={100}
                          step={1}
                          validate={composeValidators(numericInRange(0, 100))}
                        >
                          <FormText>Percentage of new cases on assignment.</FormText>
                        </Field>
                      </Col>

                      <Col>
                        <Field
                          component={TextInput}
                          type="number"
                          label="% Due Auto Assigned"
                          name="dueCasesPercentage"
                          readOnly={readOnly}
                          disabled={readOnly}
                          min={0}
                          max={100}
                          step={1}
                          validate={composeValidators(numericInRange(0, 100))}
                        >
                          <FormText>Percentage of due cases on assignment.</FormText>
                        </Field>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <Card className="rounded">
                          <CardHeader style={{ borderBottom: "none" }}>
                            <div className="d-flex flex-row justify-content-between align-items-center">
                              <h5 className="mb-0">
                                Auto Assignment Preview <small className="text-muted">(with security policy percentages.)</small>
                              </h5>

                              <Button onClick={toggleAutoAssignmentDetails}>
                                {showAutoAssignmentDetails ? <ChevronUp /> : <ChevronDown />}
                              </Button>
                            </div>
                          </CardHeader>

                          <Collapse isOpen={showAutoAssignmentDetails}>
                            <CardBody>
                              <Row>
                                <div className="d-flex flex-column items-center w-50">
                                  <div className="d-flex flex-row justify-content-center  w-100">
                                    <CasePercentageDonutChart
                                      fraction={currentNewCaseOnshoreFraction}
                                      total={currentNewCaseAmount}
                                      target={currentNewCaseAmount}
                                    />
                                  </div>

                                  <div className="d-flex flex-row justify-content-center  w-100">
                                    <small className="text-muted">Max new cases amount: {currentNewCaseAmount}</small>
                                  </div>
                                </div>

                                <div className="d-flex flex-column justify-content-center items-center w-50">
                                  <div className="d-flex flex-row justify-content-center  w-100">
                                    <CasePercentageDonutChart
                                      fraction={currentDueCaseOnshoreFraction}
                                      total={currentDueCaseAmount}
                                      target={currentDueCaseAmount}
                                    />
                                  </div>

                                  <div className="d-flex flex-row justify-content-center  w-100">
                                    <small className="text-muted">Max due cases amount: {currentDueCaseAmount}</small>
                                  </div>
                                </div>
                              </Row>
                            </CardBody>
                          </Collapse>
                        </Card>
                      </Col>
                    </Row>

                    <OnChange name="newCasesPercentage">
                      {(value, previous) => {
                        if (previous === "") return;
                        handleAutoAssignValues("newCasesPercentage", form, value);
                      }}
                    </OnChange>
                    <OnChange name="dueCasesPercentage">
                      {(value, previous) => {
                        if (previous === "") return;
                        handleAutoAssignValues("dueCasesPercentage", form, value);
                      }}
                    </OnChange>
                  </Fieldset>
                </>
              )}

              <Fieldset legend="Notes and Timestamps">
                <BlankableField
                  component={TextInput}
                  label="User Notes"
                  name="notes"
                  readOnly={readOnlyUserNotes}
                  disabled={readOnlyUserNotes}
                />
                <Field component={TextInput} label="Created At" name="createdAt" readOnly format={(v) => formatDateTime(v)} disabled />

                {values.lastSignInAt ? (
                  <Field
                    component={TextInput}
                    label="Last Logged In"
                    name="lastSignInAt"
                    readOnly
                    format={(v) => formatDateTime(v)}
                    disabled
                  />
                ) : (
                  <div className="card bg-info bg-opacity-25 d-flex flex-row p-2 my-3 gap-2">
                    <Info /> <p className="mb-0">Newly added User, awaiting for log in.</p>
                  </div>
                )}
              </Fieldset>

              <LoadingButton color="primary" loading={submitting} onClick={handleSubmit} disabled={submitting || pristine}>
                Save
              </LoadingButton>
            </>
          );
        }}
      </Form>
    </>
  );
};
